import { Dialog } from '@headlessui/react';
import { XMarkIcon, ArrowDownTrayIcon, PrinterIcon, ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import { DocumentTextIcon } from '@heroicons/react/24/solid';

interface Document {
  id: string;
  title: string;
  description: string | null;
  fileUrl: string;
  fileName: string;
  fileSize: number;
  mimeType: string;
  createdAt: string;
  project?: {
    id: string;
    name: string;
    color: string;
  } | null;
}

interface DocumentViewerModalProps {
  document: Document | null;
  isOpen: boolean;
  onClose: () => void;
}

export function DocumentViewerModal({ document, isOpen, onClose }: DocumentViewerModalProps) {
  if (!document) return null;

  const isPdf = document.mimeType === 'application/pdf';
  const isImage = document.mimeType.startsWith('image/');
  const isWord = document.mimeType.includes('word') || document.mimeType.includes('document');
  const isExcel = document.mimeType.includes('excel') || document.mimeType.includes('spreadsheet');

  const handleDownload = () => {
    window.open(document.fileUrl, '_blank');
  };

  const handlePrint = () => {
    if (isPdf) {
      const printWindow = window.open(document.fileUrl, '_blank');
      printWindow?.addEventListener('load', () => {
        printWindow.print();
      });
    }
  };

  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
      
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="mx-auto w-full max-w-6xl h-[90vh] bg-white rounded-xl shadow-xl flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b">
            <div className="flex-1 min-w-0">
              <Dialog.Title className="text-lg font-semibold text-gray-900 truncate">
                {document.title}
              </Dialog.Title>
              <p className="text-sm text-gray-500">{document.fileName}</p>
            </div>
            
            <div className="flex items-center gap-2 ml-4">
              <button
                onClick={handleDownload}
                className="p-2 text-gray-600 hover:text-primary-600 hover:bg-gray-100 rounded-lg transition-colors"
                title="Stáhnout"
              >
                <ArrowDownTrayIcon className="h-5 w-5" />
              </button>
              {isPdf && (
                <button
                  onClick={handlePrint}
                  className="p-2 text-gray-600 hover:text-primary-600 hover:bg-gray-100 rounded-lg transition-colors"
                  title="Tisk"
                >
                  <PrinterIcon className="h-5 w-5" />
                </button>
              )}
              <button
                onClick={() => window.open(document.fileUrl, '_blank')}
                className="p-2 text-gray-600 hover:text-primary-600 hover:bg-gray-100 rounded-lg transition-colors"
                title="Otevřít v novém okně"
              >
                <ArrowTopRightOnSquareIcon className="h-5 w-5" />
              </button>
              <button
                onClick={onClose}
                className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                title="Zavřít"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-hidden bg-gray-50">
            {isPdf ? (
              <iframe
                src={`${document.fileUrl}#toolbar=1&navpanes=0&scrollbar=1`}
                className="w-full h-full"
                title={document.title}
              />
            ) : isImage ? (
              <div className="w-full h-full flex items-center justify-center p-4">
                <img
                  src={document.fileUrl}
                  alt={document.title}
                  className="max-w-full max-h-full object-contain"
                />
              </div>
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <div className="text-center">
                  <DocumentTextIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                  <p className="text-lg font-medium text-gray-900 mb-2">
                    {document.title}
                  </p>
                  <p className="text-gray-600 mb-6">
                    {isWord && 'Word dokument'}
                    {isExcel && 'Excel tabulka'}
                    {!isWord && !isExcel && 'Náhled není dostupný pro tento typ souboru'}
                  </p>
                  <button
                    onClick={handleDownload}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                  >
                    <ArrowDownTrayIcon className="h-5 w-5" />
                    Stáhnout soubor
                  </button>
                </div>
              </div>
            )}
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
}
